import {
  ApiWorkout,
  RemoteConfigData,
  SegmentTypes,
} from '@/stores/workoutPlayer/types';
import WorkoutModel, { type IWorkoutModel } from '@/stores/models/WorkoutModel';
import GuidedWorkoutModel, {
  type IGuidedWorkoutModel,
} from '@/stores/models/GuidedWorkoutModel';

/**
 * Merges an API workout response with remote config data to produce
 * a fully resolved Workout that the WorkoutPlayerStore can use.
 *
 * The API returns `segment_info` as a string (the unique_name / remote config key).
 * All other metadata (name, segment_type, video URLs, preview_time) comes from
 * the remote config entry matched by that key.
 */
export function resolveWorkout(
  apiWorkout: ApiWorkout,
  remoteConfig: RemoteConfigData,
): IWorkoutModel {
  const segments = apiWorkout.segments.map((apiSeg) => {
    const uniqueName = apiSeg.segment_info;
    const configEntry = remoteConfig.segments[uniqueName];

    let videoUrls: string[] = [];
    let videoHashes: string[] = [];
    let videoSizes: number[] = [];
    if (configEntry) {
      const videoFiles = configEntry.files
        .filter((f) => f.collection_name === 'videos')
        .sort((a, b) => a.order - b.order);
      videoUrls = videoFiles.map((f) => f.url);
      videoHashes = videoFiles.map((f) => f.hash);
      videoSizes = videoFiles.map((f) => f.size);
    } else {
      console.warn(
        `[Resolver] Segment "${uniqueName}" not found in remote config (no video URLs)`,
      );
    }

    const segmentType =
      (configEntry?.segment_type as SegmentTypes) || SegmentTypes.EXERCISE;
    const isRest = segmentType === SegmentTypes.REST;

    const previewTimeMs =
      configEntry?.preview_time != null
        ? configEntry.preview_time * 1000
        : isRest
          ? 0
          : 5000;

    return {
      id: apiSeg.id,
      repetitions: apiSeg.repetitions,
      group: apiSeg.group,
      segment_info: {
        unique_name: uniqueName,
        name: configEntry?.name ?? uniqueName,
        segment_type: segmentType,
        videoUrls,
        videoHashes,
        videoSizes,
        preview_time_ms: previewTimeMs,
        completion_time_ms: apiSeg.completion_time * 1000,
      },
      started_at: apiSeg.started_at,
      completed_at: apiSeg.completed_at,
      resumed_at: apiSeg.resumed_at,
      has_skipped: apiSeg.has_skipped,
    };
  });

  return WorkoutModel.create({
    id: apiWorkout.id,
    workout_template_type: apiWorkout.workout_template_type ?? '',
    segments,
    started_at: apiWorkout.started_at,
    completed_at: apiWorkout.completed_at,
  });
}

/**
 * Resolves a guided/chaptered API workout into a GuidedWorkout.
 *
 * Guided workouts have a single segment whose `segment_info` field is a string
 * (the unique_name) that maps to a remote config entry for video URLs.
 * Chapter data comes from `user_segment_chapters` on the API segment.
 */
export function resolveGuidedWorkout(
  apiWorkout: ApiWorkout,
  remoteConfig: RemoteConfigData,
): IGuidedWorkoutModel {
  const apiSeg = apiWorkout.segments[0];
  if (!apiSeg) {
    throw new Error('[Resolver] Guided workout has no segments');
  }

  const uniqueName = apiSeg.segment_info;
  const configEntry = remoteConfig.segments[uniqueName];

  let videoUrls: string[] = [];
  let videoHashes: string[] = [];
  let videoSizes: number[] = [];
  if (configEntry) {
    const videoFiles = configEntry.files
      .filter((f) => f.collection_name === 'videos')
      .sort((a, b) => a.order - b.order);
    videoUrls = videoFiles.map((f) => f.url);
    videoHashes = videoFiles.map((f) => f.hash);
    videoSizes = videoFiles.map((f) => f.size);
  } else {
    console.warn(
      `[Resolver] Guided segment "${uniqueName}" not found in remote config`,
    );
  }

  const chapters = (apiSeg.user_segment_chapters ?? []).map((ch) => ({
    id: ch.id,
    total_progress_time_ms: ch.total_progress_time_ms,
    segment_chapter_info: {
      id: ch.segment_chapter_info.id,
      unique_name: ch.segment_chapter_info.unique_name,
      title: ch.segment_chapter_info.title,
      description: ch.segment_chapter_info.description,
      start_time_ms: ch.segment_chapter_info.start_time_ms,
      end_time_ms: ch.segment_chapter_info.end_time_ms,
    },
  }));

  return GuidedWorkoutModel.create({
    id: apiWorkout.id,
    workout_template_type: apiWorkout.workout_template_type ?? '',
    segment: {
      id: apiSeg.id,
      group: apiSeg.group,
      segment_type: SegmentTypes.CHAPTERED_ACTIVITY,
      segment_info: {
        unique_name: uniqueName,
        name: configEntry?.name ?? uniqueName,
        segment_type: SegmentTypes.CHAPTERED_ACTIVITY,
        videoUrls,
        videoHashes,
        videoSizes,
        preview_time_ms: 0,
        completion_time_ms: apiSeg.completion_time * 1000,
        equipment: (configEntry?.equipment ?? []).map((e) => e.unique_name),
      },
      completion_time_ms: apiSeg.completion_time * 1000,
      chapters,
      started_at: apiSeg.started_at,
      completed_at: apiSeg.completed_at,
      current_user_chapter_id: apiSeg.current_user_chapter_id ?? null,
    },
    started_at: apiWorkout.started_at,
    completed_at: apiWorkout.completed_at,
  });
}
